import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CrossDivPmoDivision } from '@cortex/core-types';
import { queryKeys } from '../lib/query-keys.js';
import { projectApi, memberApi, ApiError } from '../lib/api.js';
import { projectRenameApi } from '../lib/api/project-rename.js';
import type { MemberProjectHistoryLimit } from '../types/project.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクト一覧をTanStack Query経由で取得。division 指定時は指定 Div（aC/Mall/Logi Div看板）でスコープした案件、未指定時はログインユーザー所属に応じたスコープのサマリーリストを返す
 * @graph-connects /projects [calls] プロジェクト一覧取得API
 * @graph-connects projectApi.getProjects [calls] プロジェクト一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useProjects(division?: CrossDivPmoDivision) {
  return useQuery({
    queryKey: queryKeys.projects.list(division),
    queryFn: () => projectApi.getProjects(division),
  });
}

/**
 * useProjectDetail の retry 判定。404（Gen1 フォールバックに必要な確定情報）は即座に確定させ、
 * それ以外の一時的な失敗は TanStack Query の既定値相当（3回）までリトライする。
 * テストから直接検証できるよう named export にしている。
 * @graph-connects none
 */
export function shouldRetryProjectDetail(failureCount: number, error: unknown): boolean {
  return !(error instanceof ApiError && error.status === 404) && failureCount < 3;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件1件の詳細をTanStack Query経由で取得。Backlog課題キー指定で案件のステータス・健康度・進捗を返す（案件詳細ページのダッシュボードタブ用）。Gen1統合プロダクトキーでは必ず404するため、呼び出し側は404フォールバックでuseIntegratedProductDetailへ切り替える（404は即確定させ、それ以外の一時的な失敗は既定のリトライを維持する）
 * @graph-connects /projects/:pjKey [calls] 案件単体取得API
 * @graph-connects projectApi.getProject [calls] 案件単体取得APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useProjectDetail(pjKey: string) {
  return useQuery({
    queryKey: queryKeys.projects.detail(pjKey),
    queryFn: () => projectApi.getProject(pjKey),
    enabled: pjKey.length > 0,
    retry: shouldRetryProjectDetail,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 統合プロダクト（Gen1）1件の詳細をTanStack Query経由で取得。Backlog課題キー指定でGen1統合プロダクトのステータス・担当者・配下Gen2チャンク一覧を返す（Gen1案件詳細ページのダッシュボードタブ用）。useProjectDetailが404のときのみ呼び出し側がenabledで有効化するフォールバック専用フック
 * @graph-connects /projects/:pjKey/integrated-product [calls] 統合プロダクト単体取得API
 * @graph-connects projectApi.getIntegratedProduct [calls] 統合プロダクト単体取得APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useIntegratedProductDetail(pjKey: string, options: { enabled: boolean }) {
  return useQuery({
    queryKey: queryKeys.projects.integratedProduct(pjKey),
    queryFn: () => projectApi.getIntegratedProduct(pjKey),
    enabled: options.enabled && pjKey.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 子チケット一覧をTanStack Query経由で取得。親チケットID指定で紐づく子チケットのリストを返す
 * @graph-connects /projects/:parentId/children [calls] 子チケット一覧取得API
 * @graph-connects projectApi.getChildTickets [calls] 子チケット一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useChildTickets(parentId: number | null) {
  return useQuery({
    queryKey: queryKeys.projects.children(parentId ?? 0),
    queryFn: () => {
      if (parentId === null) throw new Error('parentId is required');
      return projectApi.getChildTickets(parentId);
    },
    enabled: parentId !== null,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー担当プロジェクト一覧をTanStack Query経由で取得。メンバーEmailからGen2親プロジェクトのリストを返す
 * @graph-connects /members/:email/projects [calls] メンバー担当プロジェクト取得API
 * @graph-connects memberApi.getMemberProjects [calls] メンバー担当プロジェクトAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberProjects(email: string | null) {
  return useQuery({
    queryKey: queryKeys.members.projects(email ?? ''),
    queryFn: () => {
      if (!email) throw new Error('email is required');
      return memberApi.getMemberProjects(email);
    },
    enabled: !!email,
  });
}

/**
 * 担当要件定義案件一覧を取得する。
 *
 * 開発の担当案件一覧（`useMemberProjects`）とは別エンドポイント。要件定義フェーズの母票は
 * 種別が「アサイン前」のままのものが大半で、Gen2 母票を母集団にすると一覧に出てこないため、
 * API 側で要件定義の成果物子票の担当から母票を逆引きしている。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当要件定義案件一覧（自分の要件定義成果物子票が未対応/処理中で残り、要件定義フェーズが終わっていない（要件定義レビュー完了でも基本設計 処理中/完了でもない）母票の案件キー・案件名・要件定義フェーズの進捗）をTanStack Query経由で取得
 * @graph-connects /members/:email/requirement-projects [calls] メンバー担当要件定義案件取得API
 * @graph-connects memberApi.getMemberRequirementProjects [calls] 担当要件定義案件APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberRequirementProjects(email: string | null) {
  return useQuery({
    queryKey: queryKeys.members.requirementProjects(email ?? ''),
    queryFn: () => {
      if (!email) throw new Error('email is required');
      return memberApi.getMemberRequirementProjects(email);
    },
    enabled: !!email,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー担当案件履歴（完了案件）をTanStack Query経由で取得。着手日降順で過去limit(30/50/100)件を返す
 * @graph-connects /members/:email/project-history [calls] メンバー担当案件履歴取得API
 * @graph-connects memberApi.getMemberProjectHistory [calls] メンバー担当案件履歴APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberProjectHistory(email: string | null, limit: MemberProjectHistoryLimit) {
  return useQuery({
    queryKey: queryKeys.members.projectHistory(email ?? '', limit),
    queryFn: () => {
      if (!email) throw new Error('email is required');
      return memberApi.getMemberProjectHistory(email, limit);
    },
    enabled: !!email,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビュアーリソース（現在コードレビューを担当しているエンジニアのレビュー案件数・レビュー割合）をTanStack Query経由で取得
 * @graph-connects /projects/reviewer-resources [calls] レビュアーリソース取得API
 * @graph-connects projectApi.getReviewerResources [calls] レビュアーリソースAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useReviewerResources() {
  return useQuery({
    queryKey: queryKeys.projects.reviewerResources(),
    queryFn: () => projectApi.getReviewerResources(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * 案件番号（Backlog 課題キー / 数字 / 課題 URL）から案件メタ情報を一括で引くミューテーションフック。
 *
 * PJ Sandbox の「チケットで一括取込」が、貼り付けた案件番号一覧をブロックへ変換するために使う。
 * 貼り付けるたびに結果が変わるユーザー起点の 1 回きりの操作のため、キャッシュを持つ useQuery ではなく
 * useMutation で扱う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件番号の一括照会（案件名・種別・ざっくり見積平均pt・Backlog URL）を実行する
 * @graph-connects /projects/ticket-lookup [calls] チケット一括照会API
 * @graph-connects projectApi.lookupTickets [calls] チケット一括照会APIクライアント呼び出し
 */
export function useTicketLookup() {
  return useMutation({
    mutationFn: (tokens: string[]) => projectApi.lookupTickets(tokens),
  });
}

/**
 * 案件名を変える（母票の Backlog 件名 + 案件 Slack チャンネル名）ミューテーションフック。
 *
 * 案件詳細のヘッダは BQ（project_detail_table）の件名を出しており、Backlog の変更が載るまで
 * 時間がかかる。成功したら詳細のキャッシュの件名だけを書き換え、見出しをすぐに新しい名前にする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件名の変更を実行し、案件詳細のキャッシュの件名を新しい名前に差し替える
 * @graph-connects /projects/{issueKey}/name [calls] 案件名の変更API
 * @graph-connects projectRenameApi.rename [calls] 案件名の変更APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useRenameProject(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (summary: string) => projectRenameApi.rename(pjKey, summary),
    onSuccess: (result) => {
      for (const key of [
        queryKeys.projects.detail(pjKey),
        queryKeys.projects.integratedProduct(pjKey),
      ]) {
        queryClient.setQueryData<{ summary: string } | undefined>(key, (current) =>
          current === undefined ? current : { ...current, summary: result.summary },
        );
      }
    },
  });
}
